<template>
  <v-container class="deadlines-admin">
    <v-layout row wrap>
      <v-flex xs12 md10 offset-md1>
        <h1>Deadlines &amp; registration access</h1>
        <deadlines-hold-log-tabs />
        <p class="lede">
          Festival year <strong>{{ year }}</strong>.
          Flip registration switches for early access vs public open, and set prep deadlines dancers hit in the app.
          Dates with “Not set (open)” stay open until you pick a close date.
        </p>

        <p v-if="loadError" class="error-banner" role="alert">{{ loadError }}</p>
        <p v-if="saveError" class="error-banner" role="alert">{{ saveError }}</p>
        <p v-if="saveOk" class="ok-banner" role="status">{{ saveOk }}</p>

        <div v-if="loading" class="muted">Loading…</div>

        <template v-else>
          <!-- Registration access -->
          <section class="panel">
            <h2>Registration access</h2>
            <p class="panel-hint">
              <strong>Early access</strong> = returning IABF dancers and canceled dancers with a held deposit.
              This switch turns it on or off — uncheck to close it, even after August 23.
              If you have never saved a choice, it auto-opens August 23 at 11:30am ET.
              <strong>Public open</strong> = anyone can register. Stays off until you flip it.
            </p>

            <div class="access-status" :class="'mode-' + access.mode">
              <span class="access-pill">{{ accessModeLabel }}</span>
              <span class="muted">{{ accessModeDetail }}</span>
            </div>

            <div class="switch-row">
              <label class="switch-label">
                <input v-model="earlyAccessOpen" type="checkbox" @change="onAccessDirty">
                <span>
                  <strong>Early registration open</strong>
                  <span class="switch-sub">Returners &amp; canceled only — this switch is on or off</span>
                </span>
              </label>
            </div>
            <div class="switch-row">
              <label class="switch-label">
                <input v-model="publicRegistrationOpen" type="checkbox" @change="onAccessDirty">
                <span>
                  <strong>Public registration open</strong>
                  <span class="switch-sub">Everyone — flip this for the public open event</span>
                </span>
              </label>
            </div>

            <div class="date-grid">
              <div class="field">
                <label>Planned early access date</label>
                <input v-model="earlyAccessOpensAt" type="date" @input="onAccessDirty">
                <span class="field-hint">Planning only — does not auto-flip the switch</span>
              </div>
              <div class="field">
                <label>Planned public open date</label>
                <input v-model="publicOpensAt" type="date" @input="onAccessDirty">
                <span class="field-hint">Planning only — does not auto-flip the switch</span>
              </div>
            </div>

            <div class="field">
              <label>Internal notes</label>
              <textarea v-model="accessNotes" rows="2" placeholder="e.g. Early bird email goes out Sept 1; public at 10am ET Sept 15" @input="onAccessDirty" />
            </div>

            <button
              type="button"
              class="btn primary"
              :disabled="!accessDirty || accessSaving"
              @click="saveAccess"
            >
              {{ accessSaving ? 'Saving…' : 'Save registration access' }}
            </button>
          </section>

          <section class="panel">
            <h2>WhatsApp group</h2>
            <p class="panel-hint">
              Invitation link for the festival WhatsApp group. Leave blank and dancers see a disabled
              <strong>Coming soon</strong> card (“Stay in touch the week of IABF”).
              Paste the invite URL when you’re ready — the dashboard card becomes a live join link.
            </p>
            <div class="field">
              <label>WhatsApp invitation link</label>
              <input
                v-model="whatsappInviteUrl"
                type="url"
                placeholder="https://chat.whatsapp.com/…"
                @input="onWhatsappDirty"
              >
            </div>
            <button
              type="button"
              class="btn primary"
              :disabled="!whatsappDirty || whatsappSaving"
              @click="saveWhatsapp"
            >
              {{ whatsappSaving ? 'Saving…' : 'Save WhatsApp link' }}
            </button>
          </section>

          <!-- Capacity: total tickets + onsite rooms (separate) -->
          <section class="panel">
            <h2>Registration capacity</h2>
            <p class="panel-hint">
              Two separate caps. <strong>Total tickets</strong> is overall festival headcount —
              every registration counts (onsite room, onsite roommate, or offsite).
              <strong>Onsite rooms</strong> are physical inventory for people buying their own
              Standard Dorm, Larger Dorm, or Cottage — each type has its own limit.
              Roommate and offsite tickets do <em>not</em> use a room slot;
              they only count toward total tickets. Leave blank for unlimited.
              Active registrations and unexpired 5‑minute holds both count.
            </p>

            <h3 class="limits-subhead">Overall tickets</h3>
            <div class="limits-grid">
              <div class="limit-row">
                <label>Total festival tickets (everyone)</label>
                <input
                  v-model="totalTickets"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Unlimited"
                  @input="onLimitsDirty"
                >
              </div>
            </div>

            <h3 class="limits-subhead">Onsite room inventory</h3>
            <div class="limits-grid">
              <div v-for="row in roomLimitRows" :key="row.key" class="limit-row">
                <label>{{ row.label }}</label>
                <input
                  v-model="roomLimits[row.key]"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Unlimited"
                  @input="onLimitsDirty"
                >
              </div>
            </div>
            <button
              type="button"
              class="btn primary"
              :disabled="!limitsDirty || limitsSaving"
              @click="saveLimits"
            >
              {{ limitsSaving ? 'Saving…' : 'Save capacity limits' }}
            </button>
            <button
              type="button"
              class="btn"
              :disabled="recounting"
              style="margin-left: 8px"
              @click="refreshInventory"
            >
              {{ recounting ? 'Refreshing…' : 'Release expired holds' }}
            </button>
            <p class="field-hint" style="margin-top: 8px">
              Remaining uses a running bed counter (same as checkout). Cancel, delete, and room changes
              add/subtract immediately. Expired 5‑minute holds are released here and by a one-minute sweep.
            </p>
            <p class="field-hint">
              <button
                type="button"
                class="btn-link"
                :disabled="recounting"
                @click="recountInventory"
              >Rebuild counters from the dancer list (slow — last resort)</button>
            </p>
          </section>

          <!-- Email → onsite room set-asides -->
          <section class="panel">
            <h2>Reserved rooms by email</h2>
            <p class="panel-hint">
              Link an email to an onsite room (Standard Dorm, Larger Dorm, or Cottage) so that person has a spot when they register.
              The set-aside <strong>does not use public room inventory</strong> (they can still get the room
              even if that type is sold out publicly), but it <strong>does count toward total festival tickets</strong>.
            </p>

            <div class="email-res-form">
              <div class="iabf-field-like">
                <label>Email</label>
                <input v-model.trim="resForm.email" type="email" placeholder="dancer@example.com">
              </div>
              <div class="iabf-field-like">
                <label>Room</label>
                <select v-model="resForm.room">
                  <option v-for="r in onsiteRoomChoices" :key="r.value" :value="r.value">
                    {{ r.title }}
                  </option>
                </select>
              </div>
              <div class="iabf-field-like">
                <label>Notes (optional)</label>
                <input v-model.trim="resForm.notes" type="text" placeholder="VIP, scholarship, …">
              </div>
              <button
                type="button"
                class="btn primary"
                :disabled="resSaving || !resForm.email || !resForm.room"
                @click="addEmailReservation"
              >
                {{ resSaving ? 'Saving…' : 'Reserve room for email' }}
              </button>
            </div>
            <p v-if="resError" class="error-banner" role="alert">{{ resError }}</p>

            <div v-if="!emailReservations.length" class="muted" style="margin-top: 12px">
              No email room reservations yet.
            </div>
            <table v-else class="res-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Room</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in emailReservations" :key="row.id">
                  <td>{{ row.email }}</td>
                  <td>{{ roomTitle(row.room) }}</td>
                  <td>
                    <span class="status-pill" :class="'st-' + row.status">{{ row.status }}</span>
                  </td>
                  <td class="muted">{{ row.notes || '—' }}</td>
                  <td>
                    <button
                      v-if="row.status === 'reserved'"
                      type="button"
                      class="btn-link danger"
                      :disabled="resSaving"
                      @click="releaseEmailReservation(row)"
                    >
                      Release
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="panel">
            <h2>Sold-out waitlist</h2>
            <p class="panel-hint">
              Offer a spot (link good for 7 days). If they register, they stay on this list as registered.
            </p>
            <div v-if="!waitlistEntries.length" class="muted">
              No one on the waitlist yet.
            </div>
            <table v-else class="res-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th>Offer expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in waitlistEntries" :key="row.id">
                  <td>{{ row.position }}</td>
                  <td>{{ row.email }}</td>
                  <td>{{ row.firstName || '—' }}</td>
                  <td class="muted">{{ formatWaitlistJoined(row.createdAt) }}</td>
                  <td>
                    <span class="status-pill" :class="'st-' + waitlistStatus(row)">{{ waitlistStatus(row) }}</span>
                  </td>
                  <td class="muted">
                    <template v-if="waitlistStatus(row) === 'offered'">{{ formatWaitlistJoined(row.offerExpiresAt) }}</template>
                    <template v-else>—</template>
                  </td>
                  <td class="waitlist-actions">
                    <template v-if="waitlistStatus(row) === 'waiting' || waitlistStatus(row) === 'expired'">
                      <button type="button" class="btn-link" :disabled="waitlistSaving" @click="offerWaitlist(row)">
                        Offer
                      </button>
                      <button
                        v-if="waitlistStatus(row) === 'waiting'"
                        type="button"
                        class="btn-link danger"
                        :disabled="waitlistSaving"
                        @click="declineWaitlist(row)"
                      >
                        Decline
                      </button>
                    </template>
                    <template v-else-if="waitlistStatus(row) === 'offered'">
                      <button type="button" class="btn-link" @click="copyOfferLink(row)">
                        {{ copiedWaitlistId === row.id ? 'Copied' : 'Copy link' }}
                      </button>
                      <button type="button" class="btn-link danger" :disabled="waitlistSaving" @click="declineWaitlist(row)">
                        Decline
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- Deadline tables by category -->
          <section
            v-for="cat in categories"
            :key="cat.id"
            class="panel"
          >
            <h2>{{ cat.label }}</h2>
            <div class="deadline-table">
              <div
                v-for="row in rowsFor(cat.id)"
                :key="row.key"
                class="deadline-row"
                :class="'status-' + row.status"
              >
                <div class="deadline-main">
                  <div class="deadline-label">{{ row.label }}</div>
                  <div class="deadline-desc">{{ row.description }}</div>
                  <div v-if="row.legacy2026" class="deadline-legacy">2026: {{ row.legacy2026 }}</div>
                </div>
                <div class="deadline-controls">
                  <template v-if="row.kind === 'date' && row.editable">
                    <input
                      v-model="dateEdits[row.key]"
                      type="date"
                      class="date-input"
                      @input="onDatesDirty"
                    >
                    <button
                      v-if="dateEdits[row.key]"
                      type="button"
                      class="btn-link"
                      @click="clearDate(row.key)"
                    >
                      Clear (open)
                    </button>
                  </template>
                  <template v-else-if="row.kind === 'date'">
                    <span class="deadline-value">{{ formatDate(row.effectiveIso) }}</span>
                    <span class="readonly-tag">fixed</span>
                  </template>
                  <div class="deadline-meta">
                    <span class="status-tag">{{ statusLabel(row.status) }}</span>
                    <span v-if="row.daysUntil != null && row.status === 'upcoming'" class="days-tag">
                      in {{ row.daysUntil }} day{{ row.daysUntil === 1 ? '' : 's' }}
                    </span>
                    <span v-else-if="row.daysUntil != null && row.status === 'past'" class="days-tag">
                      {{ Math.abs(row.daysUntil) }} day{{ Math.abs(row.daysUntil) === 1 ? '' : 's' }} ago
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div class="sticky-save" v-if="datesDirty">
            <button
              type="button"
              class="btn primary"
              :disabled="datesSaving"
              @click="saveDates"
            >
              {{ datesSaving ? 'Saving deadlines…' : 'Save deadline dates' }}
            </button>
            <button type="button" class="btn ghost" :disabled="datesSaving" @click="resetDates">
              Discard changes
            </button>
          </div>
        </template>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import {
  deadlineCatalog,
  DEADLINE_CATEGORIES,
  deadlineStatus,
  formatDeadlineDisplay,
  daysUntil,
  resolveRegistrationAccess
} from '@/config/iabf-deadlines'
import {
  ONSITE_ROOM_ADMIN_OPTIONS,
  lodgingChoiceTitle,
  DEFAULT_ROOM_LIMITS
} from '@/config/iabf-registration-2027'
import DeadlinesHoldLogTabs from '@/components/iabf/DeadlinesHoldLogTabs.vue'

export default {
  name: 'DeadlinesAdmin',
  mixins: [iabfAdminMixin],
  components: { DeadlinesHoldLogTabs },
  data () {
    return {
      loading: true,
      loadError: '',
      saveError: '',
      saveOk: '',
      categories: DEADLINE_CATEGORIES.filter(c => c.id !== 'registration'),
      dateEdits: {},
      savedDates: {},
      datesDirty: false,
      datesSaving: false,
      earlyAccessOpen: false,
      publicRegistrationOpen: false,
      earlyAccessOpensAt: '',
      publicOpensAt: '',
      accessNotes: '',
      accessDirty: false,
      accessSaving: false,
      access: resolveRegistrationAccess(null),
      roomLimits: { ...DEFAULT_ROOM_LIMITS },
      savedRoomLimits: { ...DEFAULT_ROOM_LIMITS },
      totalTickets: '',
      savedTotalTickets: '',
      limitsDirty: false,
      limitsSaving: false,
      recounting: false,
      emailReservations: [],
      resForm: {
        email: '',
        room: 'Standard Dorm',
        notes: ''
      },
      resSaving: false,
      resError: '',
      waitlistEntries: [],
      waitlistSaving: false,
      copiedWaitlistId: '',
      whatsappInviteUrl: '',
      savedWhatsappInviteUrl: '',
      whatsappDirty: false,
      whatsappSaving: false
    }
  },
  computed: {
    year () {
      return this.adminFestivalYear
    },
    accessModeLabel () {
      if (this.access.mode === 'public') return 'Public open'
      if (this.access.mode === 'early') return 'Early access only'
      return 'Registration closed'
    },
    accessModeDetail () {
      if (this.access.mode === 'public') {
        return 'Anyone can start a new registration.'
      }
      if (this.access.mode === 'early') {
        return 'Only returning IABF-ers and canceled dancers with a held deposit.'
      }
      return 'New registrations are closed. Flip Early registration or Public registration to open.'
    },
    catalogRows () {
      return deadlineCatalog(this.year).filter(d => d.kind === 'date')
    },
    roomLimitRows () {
      // Physical onsite rooms only — roommate / offsite share the total ticket cap
      return ONSITE_ROOM_ADMIN_OPTIONS.map(opt => ({
        key: opt.value,
        label: opt.title
      }))
    },
    onsiteRoomChoices () {
      return ONSITE_ROOM_ADMIN_OPTIONS
    }
  },
  watch: {
    adminFestivalYear () {
      this.load()
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.load()
  },
  methods: {
    formatDate (iso) {
      return formatDeadlineDisplay(iso)
    },
    statusLabel (s) {
      if (s === 'open') return 'Open'
      if (s === 'today') return 'Today'
      if (s === 'upcoming') return 'Upcoming'
      if (s === 'past') return 'Past'
      return s
    },
    effectiveFor (key, defaultIso) {
      if (Object.prototype.hasOwnProperty.call(this.dateEdits, key) && this.dateEdits[key] !== undefined) {
        return this.dateEdits[key] || null
      }
      return defaultIso
    },
    rowsFor (categoryId) {
      return this.catalogRows
        .filter(d => d.category === categoryId)
        .map(d => {
          const effectiveIso = this.effectiveFor(d.key, d.defaultIso)
          const status = deadlineStatus(effectiveIso)
          return {
            ...d,
            effectiveIso,
            status,
            daysUntil: daysUntil(effectiveIso)
          }
        })
    },
    async load () {
      this.loading = true
      this.loadError = ''
      this.saveOk = ''
      try {
        const result = await this.$store.dispatch('loadIabfDeadlinesConfig', this.year)
        const overrides = (result && result.deadlines) || {}
        const edits = {}
        deadlineCatalog(this.year).forEach(d => {
          if (d.kind !== 'date') return
          if (Object.prototype.hasOwnProperty.call(overrides, d.key)) {
            edits[d.key] = overrides[d.key] || ''
          } else {
            edits[d.key] = d.defaultIso || ''
          }
        })
        this.dateEdits = edits
        this.savedDates = { ...edits }
        this.datesDirty = false

        const access = (result && result.registration) || resolveRegistrationAccess(null)
        const raw = (result && result.rawRegistration) || {}
        this.access = access
        this.publicRegistrationOpen = access.publicRegistrationOpen
        this.earlyAccessOpen = Object.prototype.hasOwnProperty.call(raw, 'earlyAccessOpen')
          ? !!raw.earlyAccessOpen
          : !!access.earlyAccessOpen
        this.publicOpensAt = access.publicOpensAt || ''
        this.earlyAccessOpensAt = access.earlyAccessOpensAt || ''
        this.accessNotes = access.notes || ''
        this.accessDirty = false

        try {
          const links = await this.$store.dispatch('loadIabfCommunityLinks', this.year)
          this.whatsappInviteUrl = (links && links.whatsappInviteUrl) || ''
          this.savedWhatsappInviteUrl = this.whatsappInviteUrl
          this.whatsappDirty = false
        } catch (linkErr) {
          console.warn('community links load failed', linkErr)
          this.whatsappInviteUrl = ''
          this.savedWhatsappInviteUrl = ''
          this.whatsappDirty = false
        }

        // Capacity: onsite room inventory + total tickets (separate config doc)
        try {
          const result = await this.$store.dispatch('loadIabfRoomLimits', this.year)
          const limits = (result && result.limits) || {}
          const next = { ...DEFAULT_ROOM_LIMITS }
          Object.keys(next).forEach(k => {
            if (limits[k] !== undefined && limits[k] !== null && limits[k] !== '') {
              next[k] = String(limits[k])
            } else {
              next[k] = ''
            }
          })
          this.roomLimits = next
          this.savedRoomLimits = { ...next }
          const total = result && result.totalTickets
          this.totalTickets = (total !== null && total !== undefined && total !== '')
            ? String(total)
            : ''
          this.savedTotalTickets = this.totalTickets
          this.limitsDirty = false
        } catch (limErr) {
          console.warn('room limits load failed', limErr)
        }

        try {
          this.emailReservations = await this.$store.dispatch('loadIabfRoomEmailReservations', this.year)
        } catch (resErr) {
          console.warn('email room reservations load failed', resErr)
          this.emailReservations = []
        }

        try {
          this.waitlistEntries = await this.$store.dispatch('loadIabfRegistrationWaitlist', this.year)
        } catch (wlErr) {
          console.warn('waitlist load failed', wlErr)
          this.waitlistEntries = []
        }
      } catch (e) {
        console.error(e)
        this.loadError = (e && e.message) || 'Could not load deadlines config.'
      }
      this.loading = false
    },
    roomTitle (room) {
      const opt = ONSITE_ROOM_ADMIN_OPTIONS.find(o => o.value === room)
      if (opt) {
        return opt.title
      }
      return lodgingChoiceTitle(room)
    },
    formatWaitlistJoined (ms) {
      const n = Number(ms)
      if (!n) {
        return '—'
      }
      return new Date(n).toLocaleString()
    },
    waitlistStatus (row) {
      return (row && row.status) || 'waiting'
    },
    offerUrl (row) {
      const token = row && row.offerToken
      if (!token) {
        return ''
      }
      const local = typeof window !== 'undefined' &&
        /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
      const origin = local ? window.location.origin : 'https://dashboard.iabf.dance'
      return origin + '/register?offer=' + token
    },
    async offerWaitlist (row) {
      if (!row || !row.id) {
        return
      }
      this.waitlistSaving = true
      this.resError = ''
      try {
        const result = await this.$store.dispatch('offerIabfRegistrationWaitlist', {
          year: this.year,
          id: row.id
        })
        this.waitlistEntries = await this.$store.dispatch('loadIabfRegistrationWaitlist', this.year)
        const url = (result && result.offerUrl) || this.offerUrl(row)
        try {
          await navigator.clipboard.writeText(url)
          this.copiedWaitlistId = row.id
        } catch (e) {
          window.prompt('Copy this offer link:', url)
        }
        this.saveOk = 'Offer link copied. It expires in 7 days.'
      } catch (e) {
        console.error(e)
        this.resError = (e && e.message) || 'Could not create offer.'
      }
      this.waitlistSaving = false
    },
    async declineWaitlist (row) {
      if (!row || !row.id) {
        return
      }
      this.waitlistSaving = true
      this.resError = ''
      try {
        await this.$store.dispatch('declineIabfRegistrationWaitlist', {
          year: this.year,
          id: row.id
        })
        this.waitlistEntries = await this.$store.dispatch('loadIabfRegistrationWaitlist', this.year)
        this.saveOk = 'Marked declined.'
      } catch (e) {
        console.error(e)
        this.resError = (e && e.message) || 'Could not decline.'
      }
      this.waitlistSaving = false
    },
    async copyOfferLink (row) {
      const url = this.offerUrl(row)
      if (!url) {
        return
      }
      try {
        await navigator.clipboard.writeText(url)
        this.copiedWaitlistId = row.id
      } catch (e) {
        window.prompt('Copy this offer link:', url)
      }
    },
    async addEmailReservation () {
      this.resSaving = true
      this.resError = ''
      this.saveOk = ''
      try {
        await this.$store.dispatch('createIabfRoomEmailReservation', {
          year: this.year,
          email: this.resForm.email,
          room: this.resForm.room,
          notes: this.resForm.notes
        })
        this.resForm = { email: '', room: 'Standard Dorm', notes: '' }
        this.emailReservations = await this.$store.dispatch('loadIabfRoomEmailReservations', this.year)
        this.saveOk = 'Room reserved for that email.'
      } catch (e) {
        console.error(e)
        this.resError = (e && e.message) || 'Could not save reservation.'
      }
      this.resSaving = false
    },
    async releaseEmailReservation (row) {
      if (!row || !row.id) return
      // eslint-disable-next-line no-alert
      if (!window.confirm('Release the reserved room for ' + row.email + '?')) return
      this.resSaving = true
      this.resError = ''
      try {
        await this.$store.dispatch('releaseIabfRoomEmailReservation', {
          year: this.year,
          id: row.id
        })
        this.emailReservations = await this.$store.dispatch('loadIabfRoomEmailReservations', this.year)
        this.saveOk = 'Reservation released.'
      } catch (e) {
        console.error(e)
        this.resError = (e && e.message) || 'Could not release reservation.'
      }
      this.resSaving = false
    },
    onDatesDirty () {
      this.datesDirty = true
      this.saveOk = ''
    },
    onLimitsDirty () {
      this.limitsDirty = true
      this.saveOk = ''
    },
    onAccessDirty () {
      this.accessDirty = true
      this.saveOk = ''
      // Live preview of mode
      this.access = resolveRegistrationAccess({
        publicRegistrationOpen: this.publicRegistrationOpen,
        earlyAccessOpen: this.earlyAccessOpen,
        publicOpensAt: this.publicOpensAt,
        earlyAccessOpensAt: this.earlyAccessOpensAt,
        notes: this.accessNotes
      })
    },
    async saveLimits () {
      this.limitsSaving = true
      this.saveError = ''
      this.saveOk = ''
      try {
        const limits = {}
        Object.keys(this.roomLimits).forEach(k => {
          const raw = this.roomLimits[k]
          if (raw === '' || raw === null || raw === undefined) {
            limits[k] = null
          } else {
            const n = parseInt(raw, 10)
            limits[k] = Number.isFinite(n) && n >= 0 ? n : null
          }
        })
        let totalTickets = null
        if (this.totalTickets !== '' && this.totalTickets != null) {
          const n = parseInt(this.totalTickets, 10)
          totalTickets = Number.isFinite(n) && n >= 0 ? n : null
        }
        await this.$store.dispatch('saveIabfRoomLimits', {
          year: this.year,
          limits,
          totalTickets
        })
        this.savedRoomLimits = { ...this.roomLimits }
        this.savedTotalTickets = this.totalTickets
        this.limitsDirty = false
        this.saveOk = 'Capacity limits saved for ' + this.year + '.'
      } catch (e) {
        console.error(e)
        this.saveError = (e && e.message) || 'Could not save capacity limits.'
      }
      this.limitsSaving = false
    },
    async refreshInventory () {
      this.recounting = true
      this.saveError = ''
      this.saveOk = ''
      try {
        await this.$store.dispatch('refreshIabfRoomInventory')
        this.saveOk = 'Expired holds released. Remaining is from the running counters.'
      } catch (e) {
        console.error(e)
        this.saveError = (e && e.message) || 'Could not refresh remaining.'
      }
      this.recounting = false
    },
    async recountInventory () {
      this.recounting = true
      this.saveError = ''
      this.saveOk = ''
      try {
        await this.$store.dispatch('recountIabfRoomInventory')
        this.saveOk = 'Counters rebuilt from current registrations and live holds.'
      } catch (e) {
        console.error(e)
        this.saveError = (e && e.message) || 'Could not rebuild counters.'
      }
      this.recounting = false
    },
    clearDate (key) {
      this.$set(this.dateEdits, key, '')
      this.onDatesDirty()
    },
    resetDates () {
      this.dateEdits = { ...this.savedDates }
      this.datesDirty = false
    },
    async saveDates () {
      this.datesSaving = true
      this.saveError = ''
      this.saveOk = ''
      try {
        const overrides = {}
        deadlineCatalog(this.year).forEach(d => {
          if (d.kind !== 'date' || !d.editable) return
          const v = this.dateEdits[d.key]
          overrides[d.key] = v ? String(v).slice(0, 10) : null
        })
        await this.$store.dispatch('saveIabfDeadlineOverrides', {
          year: this.year,
          overrides
        })
        this.savedDates = { ...this.dateEdits }
        this.datesDirty = false
        this.saveOk = 'Deadline dates saved for ' + this.year + '.'
      } catch (e) {
        console.error(e)
        this.saveError = (e && e.message) || 'Could not save deadlines.'
      }
      this.datesSaving = false
    },
    async saveAccess () {
      this.accessSaving = true
      this.saveError = ''
      this.saveOk = ''
      try {
        const access = await this.$store.dispatch('saveIabfRegistrationAccess', {
          year: this.year,
          publicRegistrationOpen: this.publicRegistrationOpen,
          earlyAccessOpen: this.earlyAccessOpen,
          publicOpensAt: this.publicOpensAt || null,
          earlyAccessOpensAt: this.earlyAccessOpensAt || null,
          notes: this.accessNotes
        })
        this.access = access
        this.accessDirty = false
        this.saveOk = 'Registration access saved for ' + this.year + '.'
      } catch (e) {
        console.error(e)
        this.saveError = (e && e.message) || 'Could not save registration access.'
      }
      this.accessSaving = false
    },
    onWhatsappDirty () {
      this.whatsappDirty = true
      this.saveOk = ''
      this.saveError = ''
    },
    async saveWhatsapp () {
      this.whatsappSaving = true
      this.saveError = ''
      this.saveOk = ''
      try {
        const saved = await this.$store.dispatch('saveIabfCommunityLinks', {
          year: this.year,
          whatsappInviteUrl: this.whatsappInviteUrl
        })
        this.whatsappInviteUrl = (saved && saved.whatsappInviteUrl) || ''
        this.savedWhatsappInviteUrl = this.whatsappInviteUrl
        this.whatsappDirty = false
        this.saveOk = this.whatsappInviteUrl
          ? 'WhatsApp link saved — the dashboard card is live.'
          : 'WhatsApp link cleared — dashboard shows Coming soon.'
      } catch (e) {
        console.error(e)
        this.saveError = (e && e.message) || 'Could not save WhatsApp link.'
      }
      this.whatsappSaving = false
    }
  }
}
</script>

<style scoped>
.deadlines-admin {
  max-width: 920px;
  padding-bottom: 100px;
}
h1 {
  font-family: Georgia, serif;
  font-weight: 400;
  font-size: 32px;
  margin: 8px 0 8px;
}
.lede {
  color: #5c5a63;
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 24px;
}
.panel {
  background: #fff;
  border: 1px solid #e3e0e8;
  border-radius: 12px;
  padding: 20px 20px 24px;
  margin-bottom: 20px;
}
.panel h2 {
  font-size: 18px;
  margin: 0 0 8px;
}
.panel-hint {
  font-size: 14px;
  color: #5c5a63;
  line-height: 1.45;
  margin: 0 0 16px;
}
.access-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  background: #f6f4f1;
}
.access-status.mode-public { background: #e8f2ea; }
.access-status.mode-early { background: #f5efe0; }
.access-status.mode-closed { background: #f3e8e8; }
.access-pill {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
}
.switch-row {
  margin-bottom: 12px;
}
.switch-label {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
  font-size: 15px;
}
.switch-label input {
  margin-top: 4px;
  width: 18px;
  height: 18px;
  accent-color: #8a957d;
}
.switch-sub {
  display: block;
  font-size: 13px;
  color: #8a8792;
  font-weight: 400;
  margin-top: 2px;
}
.date-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 16px 0;
}
@media (max-width: 700px) {
  .date-grid { grid-template-columns: 1fr; }
}
.field label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a8792;
  margin-bottom: 4px;
}
.field input[type="date"],
.field textarea,
.date-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d5d1db;
  border-radius: 8px;
  font: inherit;
  font-size: 15px;
}
.field-hint {
  display: block;
  font-size: 12px;
  color: #8a8792;
  margin-top: 4px;
}
.deadline-table {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.deadline-row {
  display: grid;
  grid-template-columns: 1fr minmax(200px, 280px);
  gap: 16px;
  padding: 14px 0;
  border-top: 1px solid #eeeaf2;
}
.deadline-row:first-child { border-top: none; }
.deadline-label {
  font-weight: 700;
  font-size: 15px;
  color: #1c1b1f;
}
.deadline-desc {
  font-size: 13px;
  color: #5c5a63;
  margin-top: 2px;
  line-height: 1.4;
}
.deadline-legacy {
  font-size: 12px;
  color: #8a8792;
  margin-top: 4px;
}
.deadline-controls {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}
.deadline-value {
  font-weight: 600;
  font-size: 15px;
}
.readonly-tag {
  font-size: 11px;
  color: #8a8792;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.deadline-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.status-tag, .days-tag {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eeeaf2;
  color: #5c5a63;
}
.status-upcoming .status-tag { background: #e8f0fa; color: #2a5a8c; }
.status-today .status-tag { background: #f5efe0; color: #8a6a28; }
.status-past .status-tag { background: #eee; color: #888; }
.status-open .status-tag { background: #e8f2ea; color: #2f6b4f; }
.btn {
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  font-size: 14px;
}
.btn.primary {
  background: #8a957d;
  color: #fff;
}
.btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn.ghost {
  background: transparent;
  color: #5c5a63;
  border: 1px solid #d5d1db;
}
.btn-link {
  border: none;
  background: none;
  color: #8a957d;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
.sticky-save {
  position: sticky;
  bottom: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e3e0e8;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(28, 27, 31, 0.1);
}
.error-banner {
  background: #fde8e8;
  color: #8b2e2e;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}
.ok-banner {
  background: #e8f2ea;
  color: #2f6b4f;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}
.muted { color: #8a8792; }
.limits-subhead {
  margin: 8px 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: #1c1b1f;
}
.email-res-form {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr auto;
  gap: 10px 12px;
  align-items: end;
  margin-bottom: 16px;
}
.iabf-field-like label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #5c5a63;
}
.iabf-field-like input,
.iabf-field-like select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d5d1db;
  border-radius: 8px;
  font: inherit;
  box-sizing: border-box;
}
.res-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-top: 8px;
}
.res-table th,
.res-table td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #ebe8f0;
}
.res-table th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a8792;
}
.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: capitalize;
}
.st-reserved {
  background: rgba(138, 149, 125, 0.2);
  color: #3d4a35;
}
.st-claimed {
  background: #e8f2ea;
  color: #2f6b4f;
}
.st-released {
  background: #f0eef3;
  color: #8a8792;
}
.st-waiting {
  background: rgba(138, 149, 125, 0.2);
  color: #3d4a35;
}
.st-offered {
  background: #e8f2ea;
  color: #2f6b4f;
}
.st-expired {
  background: #f7f1e3;
  color: #8a6a2f;
}
.st-declined {
  background: #fdeeee;
  color: #8b2e2e;
}
.st-registered {
  background: #e8f2ea;
  color: #2f6b4f;
}
.waitlist-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}
.btn-link.danger {
  color: #b54a4a;
}
@media (max-width: 900px) {
  .email-res-form {
    grid-template-columns: 1fr;
  }
}
.limits-grid {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 10px 16px;
  align-items: center;
  margin-bottom: 16px;
  max-width: 520px;
}
.limit-row label {
  font-size: 14px;
  font-weight: 600;
  color: #1c1b1f;
}
.limit-row input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d5d1db;
  border-radius: 8px;
  font: inherit;
}
@media (max-width: 700px) {
  .deadline-row {
    grid-template-columns: 1fr;
  }
  .limits-grid {
    grid-template-columns: 1fr;
  }
}
</style>
